import i18n from "i18next";

import { natcatAnalyticsDefaultLayer } from "./NatcatMapFillLayers";
import { InlandFloodNatcatLegend } from "../legends/inlandFlood/InlandFloodNatcatLegend";
import { InlandFloodNatcatWeightedLegend } from "../legends/inlandFlood/InlandFloodNatcatWeightedLegend";
import { InlandFloodIspraTIVLegend } from "../legends/InlandFloodIspraTIVLegend";
import { InlandFloodIspraRiskLegend } from "../legends/InlandFloodIspraRiskLegend";
import { InlandFloodHowden56Legend } from "../legends/inlandFlood/InlandFloodHowden56Legend";
import {
  redShades,
  getNatCatAnalyticsInlandTivFillLayer,
  getNatCatAnalyticsInlandHex6TivFillLayer,
  getNatCatAnalyticsInlandHex7TivFillLayer,
  natCatAnalyticsInlandHex6TivAndRiskLevelFillLayer,
  natCatAnalyticsInlandHex7TivAndRiskLevelFillLayer,
  natCatAnalyticsInlandTivAndInlandRiskFillLayer,
  natCatAnalyticsInlandHowdenWeightedFillLayer,
} from "./NatcatMapFillLayers";

import { riskEvaluationMapStyles } from "../map/Constants";
import { geojsonComuni, geojsonH6, geojsonH7 } from "./NatCatGeojsons";
import { PermissionIdEnum } from "../util/types/permissions/permissionIdEnum";

export const getInlandFloodHowdenLayers = () => ({
  inlandFloodIspra: {
    id: "inlandFloodIspra",
    label: "Inland Flood Ispra Risk",
    permissions: [PermissionIdEnum.InlandFloodIspra],
    legend: <InlandFloodIspraRiskLegend />,
    mapLayer: riskEvaluationMapStyles.inlandFloodIspraMapboxStyle,
    mapGeojsonLayer: natcatAnalyticsDefaultLayer,
  },
  inlandFloodIspraTiv: {
    id: "inlandFloodIspraTiv",
    label: "Inland Flood Ispra TIV",
    permissions: [PermissionIdEnum.InlandFloodIspra],
    mapGeojsonLayer: getNatCatAnalyticsInlandTivFillLayer("TIVMediumOrHigh"),
    legend: (
      <InlandFloodIspraTIVLegend
        title={`${i18n.t("riskAssessment.inlandFloodIspra")}, TIV`}
        indexValues={[0, 35, 150, 450, 1500]}
        areaUnit={"commune"}
        dataFrom="IdroGEO ISPRA (v. 4.0 –2020-2021)"
        riskRangeFrom={"Medium"}
        riskRangeTo={"High"}
      />
    ),
    geojsonUrl: geojsonComuni,
    geojsonProp: "TIVMediumOrHigh",
  },
  inlandFloodIspraTivHex6: {
    id: "inlandFloodIspraTivHex6",
    label: "Inland Flood Ispra TIV Hex 6",
    permissions: [PermissionIdEnum.InlandFloodIspra],
    mapGeojsonLayer: getNatCatAnalyticsInlandHex6TivFillLayer("TIVMediumOrHigh"),
    legend: (
      <InlandFloodIspraTIVLegend
        title={`${i18n.t("riskAssessment.inlandFloodIspra")}, TIV, Hex 6`}
        indexValues={[3, 10, 33, 100, 330]}
        areaUnit={"hexagon of 36 km²"}
        dataFrom="IdroGEO ISPRA (v. 4.0 –2020-2021)"
        riskRangeFrom={"Medium"}
        riskRangeTo={"High"}
      />
    ),
    geojsonUrl: geojsonH6,
    geojsonProp: "TIVMediumOrHigh",
  },
  inlandFloodIspraTivHex7: {
    id: "inlandFloodIspraTivHex7",
    label: "Inland Flood Ispra TIV Hex 7",
    permissions: [PermissionIdEnum.InlandFloodIspra],
    mapGeojsonLayer: getNatCatAnalyticsInlandHex7TivFillLayer("TIVMediumOrHigh"),
    legend: (
      <InlandFloodIspraTIVLegend
        title={`${i18n.t("riskAssessment.inlandFloodIspra")}, TIV, Hex 7`}
        indexValues={[1, 3, 10, 33, 100]}
        areaUnit={"hexagon of 5 km²"}
        dataFrom="IdroGEO ISPRA (v. 4.0 –2020-2021)"
        riskRangeFrom={"Medium"}
        riskRangeTo={"High"}
      />
    ),
    geojsonUrl: geojsonH7,
    geojsonProp: "TIVMediumOrHigh",
  },
  inlandFloodIspraTivHex6AndInlandRisk: {
    id: "inlandFloodIspraTivHex6AndInlandRiskLayer",
    label: "Inland Flood Ispra TIV Hex 6 and Inland Flood Risk",
    permissions: [PermissionIdEnum.InlandFloodIspra],
    mapGeojsonLayer: natCatAnalyticsInlandHex6TivAndRiskLevelFillLayer,
    legend: <InlandFloodIspraRiskLegend />,
    secondLegend: (
      <InlandFloodIspraTIVLegend
        title={`${i18n.t("riskAssessment.inlandFloodIspra")}, TIV, Hex 6`}
        indexValues={[3, 10, 33, 100, 330]}
        colorValues={redShades}
        areaUnit={"hexagon of 36 km²"}
        dataFrom="IdroGEO ISPRA (v. 4.0 –2020-2021)"
        riskRangeFrom={"Medium"}
        riskRangeTo={"High"}
      />
    ),
    geojsonUrl: geojsonH6,
    geojsonProp: "TIVMediumOrHigh",
    mapLayer: riskEvaluationMapStyles.inlandFloodIspraMapboxStyle,
  },
  inlandFloodIspraTivHex7AndInlandRisk: {
    id: "inlandFloodIspraTivHex7AndInlandRiskLayer",
    label: "Inland Flood Ispra TIV Hex 7 and Inland Flood Risk",
    permissions: [PermissionIdEnum.InlandFloodIspra],
    mapGeojsonLayer: natCatAnalyticsInlandHex7TivAndRiskLevelFillLayer,
    legend: <InlandFloodIspraRiskLegend />,
    secondLegend: (
      <InlandFloodIspraTIVLegend
        title={`${i18n.t("riskAssessment.inlandFloodIspra")}, TIV, Hex 7`}
        indexValues={[1, 3, 10, 33, 100]}
        colorValues={redShades}
        areaUnit={"hexagon of 5 km²"}
        dataFrom="IdroGEO ISPRA (v. 4.0 –2020-2021)"
        riskRangeFrom={"Medium"}
        riskRangeTo={"High"}
      />
    ),
    geojsonUrl: geojsonH7,
    geojsonProp: "TIVMediumOrHigh",
    mapLayer: riskEvaluationMapStyles.inlandFloodIspraMapboxStyle,
  },
  inlandFloodIspraTivAndInlandRisk: {
    id: "inlandFloodIspraTivAndInlandRisk",
    label: "Inland Flood Ispra TIV and Inland Flood Risk",
    permissions: [PermissionIdEnum.InlandFloodIspra],
    mapGeojsonLayer: natCatAnalyticsInlandTivAndInlandRiskFillLayer,
    legend: <InlandFloodIspraRiskLegend />,
    secondLegend: (
      <InlandFloodIspraTIVLegend
        title={`${i18n.t("riskAssessment.inlandFloodIspra")}, TIV`}
        indexValues={[0, 35, 150, 450, 1500]}
        colorValues={redShades}
        areaUnit={"commune"}
        dataFrom="IdroGEO ISPRA (v. 4.0 –2020-2021)"
        riskRangeFrom={"Medium"}
        riskRangeTo={"High"}
      />
    ),
    geojsonUrl: geojsonComuni,
    geojsonProp: "TIVMediumOrHigh",
    mapLayer: riskEvaluationMapStyles.inlandFloodIspraMapboxStyle,
  },
  inlandFloodHowdenIndex: {
    id: "inlandFloodHowdenIndex",
    label: "Inland Flood Howden Risk",
    permissions: [PermissionIdEnum.InlandFloodHowden],
    legend: <InlandFloodNatcatLegend />,
    mapLayer: riskEvaluationMapStyles.inlandFloodNatcatRiskStyle,
    mapGeojsonLayer: natcatAnalyticsDefaultLayer,
  },

  inlandFloodHowdenWeightedIndex: {
    id: "inlandFloodHowdenWeightedIndex",
    label: "Inland Flood Howden Weighted Risk",
    permissions: [PermissionIdEnum.InlandFloodHowden],
    legend: <InlandFloodNatcatWeightedLegend />,

    mapGeojsonLayer: natCatAnalyticsInlandHowdenWeightedFillLayer,

    geojsonUrl:
      "https://d24ye0ax7gglwd.cloudfront.net/cat-weighted-average/italy_weighted_rate_v2_202410.geojson",
    geojsonProp: "weighted_howden_rate",
  },

  // Howden 0 (values 1-10)

  inlandFloodHowdenTiv: {
    id: "inlandFloodHowdenTiv",
    label: "Inland Flood Howden TIV",
    permissions: [PermissionIdEnum.InlandFloodHowden],
    mapGeojsonLayer: getNatCatAnalyticsInlandTivFillLayer("HIF_TIV_P3_10"),
    legend: (
      <InlandFloodIspraTIVLegend
        title={`${i18n.t("riskExposureMapExplained.inlandFloodHowden")}, TIV`}
        indexValues={[0, 35, 150, 450, 1500]}
        areaUnit={"commune"}
        dataFrom="RMS and Howden"
        riskRangeFrom={3}
        riskRangeTo={10}
      />
    ),
    geojsonUrl: geojsonComuni,
    geojsonProp: "HIF_TIV_P3_10",
  },
  inlandFloodHowdenTivHex6: {
    id: "inlandFloodHowdenTivHex6",
    label: "Inland Flood Howden TIV Hex 6",
    permissions: [PermissionIdEnum.InlandFloodHowden],
    mapGeojsonLayer: getNatCatAnalyticsInlandHex6TivFillLayer("HIF_TIV_P3_10"),
    legend: (
      <InlandFloodIspraTIVLegend
        title={`${i18n.t("riskExposureMapExplained.inlandFloodHowden")}, TIV, Hex 6`}
        indexValues={[3, 10, 33, 100, 330]}
        areaUnit={"hexagon of 36 km²"}
        dataFrom="RMS and Howden"
        riskRangeFrom={3}
        riskRangeTo={10}
      />
    ),
    geojsonUrl: geojsonH6,
    geojsonProp: "HIF_TIV_P3_10",
  },
  inlandFloodHowdenTivHex7: {
    id: "inlandFloodHowdenTivHex7",
    label: "Inland Flood Howden TIV Hex 7",
    permissions: [PermissionIdEnum.InlandFloodHowden],
    mapGeojsonLayer: getNatCatAnalyticsInlandHex7TivFillLayer("HIF_TIV_P3_10"),
    legend: (
      <InlandFloodIspraTIVLegend
        title={`${i18n.t("riskExposureMapExplained.inlandFloodHowden")}, TIV, Hex 7`}
        indexValues={[1, 3, 10, 33, 100]}
        areaUnit={"hexagon of 5 km²"}
        dataFrom="RMS and Howden"
        riskRangeFrom={3}
        riskRangeTo={10}
      />
    ),
    geojsonUrl: geojsonH7,
    geojsonProp: "HIF_TIV_P3_10",
  },

  // Howden 2 (values 1-56)

  inlandFloodHowden56Index: {
    id: "inlandFloodHowden56Index",
    label: "Inland Flood Howden 2 Risk",
    permissions: [PermissionIdEnum.InlandFloodHowden56],
    legend: <InlandFloodHowden56Legend />,
    mapLayer: riskEvaluationMapStyles.inlandFloodNatcat56RiskStyle,
    mapGeojsonLayer: natcatAnalyticsDefaultLayer,
  },

  inlandFloodHowden56Tiv: {
    id: "inlandFloodHowden56Tiv",
    label: "Inland Flood Howden 2 TIV",
    permissions: [PermissionIdEnum.InlandFloodHowden56],
    mapGeojsonLayer: getNatCatAnalyticsInlandTivFillLayer("H56IF_TIV_P6_56"),
    legend: (
      <InlandFloodIspraTIVLegend
        title={`${i18n.t("riskExposureMapExplained.inlandFloodHowden56")}, TIV`}
        indexValues={[0, 35, 150, 450, 1500]}
        areaUnit={"commune"}
        dataFrom="RMS and Howden"
        riskRangeFrom={6}
        riskRangeTo={58}
      />
    ),
    geojsonUrl: geojsonComuni,
    geojsonProp: "H56IF_TIV_P6_56",
  },
  inlandFloodHowden56TivHex6: {
    id: "inlandFloodHowden56TivHex6",
    label: "Inland Flood Howden 2 TIV Hex 6",
    permissions: [PermissionIdEnum.InlandFloodHowden56],
    mapGeojsonLayer: getNatCatAnalyticsInlandHex6TivFillLayer("H56IF_TIV_P6_56"),
    legend: (
      <InlandFloodIspraTIVLegend
        title={`${i18n.t("riskExposureMapExplained.inlandFloodHowden56")}, TIV, Hex 6`}
        indexValues={[3, 10, 33, 100, 330]}
        areaUnit={"hexagon of 36 km²"}
        dataFrom="RMS and Howden"
        riskRangeFrom={6}
        riskRangeTo={58}
      />
    ),
    geojsonUrl: geojsonH6,
    geojsonProp: "H56IF_TIV_P6_56",
  },
  inlandFloodHowden56TivHex7: {
    id: "inlandFloodHowden56TivHex7",
    label: "Inland Flood Howden 2 TIV Hex 7",
    permissions: [PermissionIdEnum.InlandFloodHowden56],
    mapGeojsonLayer: getNatCatAnalyticsInlandHex7TivFillLayer("H56IF_TIV_P6_56"),
    legend: (
      <InlandFloodIspraTIVLegend
        title={`${i18n.t("riskExposureMapExplained.inlandFloodHowden56")}, TIV, Hex 7`}
        indexValues={[1, 3, 10, 33, 100]}
        areaUnit={"hexagon of 5 km²"}
        dataFrom="RMS and Howden"
        riskRangeFrom={6}
        riskRangeTo={58}
      />
    ),
    geojsonUrl: geojsonH7,
    geojsonProp: "H56IF_TIV_P6_56",
  },
});
